// California Rating Areas utility for Word & Brown "AREA N" labels
//
// This file provides:
// - Canonical county-to-area mapping for CA rating areas (1..19)
// - Optional ZIP-to-area mapping (empty by default, can be extended)
// - Lookups by county or ZIP
// - Helpers to register additional ZIP mappings at runtime
//
// Notes on ZIPs:
// - California rating areas are defined primarily by county. Los Angeles County
//   is split into two areas (15 and 16) based on ZIP boundaries. Because a full
//   authoritative ZIP list is large and can change, this util exposes runtime
//   registration functions so you can load provider-specific ZIP mappings (e.g.,
//   from Word & Brown) and keep this file maintainable.

export type AreaId = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18 | 19;

export interface RatingAreaInfo {
  id: AreaId;
  label: string; // e.g., "AREA 2"
  counties: string[]; // canonical county names
  // Optional ZIP data bound to this area
  zips?: string[]; // full 5-digit ZIPs
  zipPrefixes?: string[]; // 3-digit prefixes (e.g., for LA split)
}

// Canonical county mapping for CA rating areas (individual/small-group standard)
// Source: Covered CA / CMS rating regions. County lists are stable over time.
export const CA_RATING_AREAS: RatingAreaInfo[] = [
  {
    id: 1,
    label: "AREA 1",
    counties: [
      "Alpine",
      "Amador",
      "Butte",
      "Calaveras",
      "Colusa",
      "Del Norte",
      "Glenn",
      "Humboldt",
      "Lake",
      "Lassen",
      "Mendocino",
      "Modoc",
      "Nevada",
      "Plumas",
      "Shasta",
      "Sierra",
      "Siskiyou",
      "Sutter",
      "Tehama",
      "Trinity",
      "Tuolumne",
      "Yuba",
    ],
  },
  { id: 2, label: "AREA 2", counties: ["Napa", "Sonoma", "Solano", "Marin"] },
  { id: 3, label: "AREA 3", counties: ["Sacramento", "Placer", "El Dorado", "Yolo"] },
  { id: 4, label: "AREA 4", counties: ["San Francisco"] },
  { id: 5, label: "AREA 5", counties: ["Contra Costa"] },
  { id: 6, label: "AREA 6", counties: ["Alameda"] },
  { id: 7, label: "AREA 7", counties: ["Santa Clara"] },
  { id: 8, label: "AREA 8", counties: ["San Mateo"] },
  { id: 9, label: "AREA 9", counties: ["Santa Cruz", "Monterey", "San Benito"] },
  { id: 10, label: "AREA 10", counties: ["San Joaquin", "Stanislaus", "Merced", "Mariposa", "Tulare"] },
  { id: 11, label: "AREA 11", counties: ["Madera", "Fresno", "Kings"] },
  { id: 12, label: "AREA 12", counties: ["San Luis Obispo", "Santa Barbara", "Ventura"] },
  { id: 13, label: "AREA 13", counties: ["Mono", "Inyo", "Imperial"] },
  { id: 14, label: "AREA 14", counties: ["Kern"] },
  // Los Angeles County is split into 15 and 16 by ZIP boundaries
  {
    id: 15,
    label: "AREA 15",
    counties: ["Los Angeles"],
    zipPrefixes: ["906", "907", "908", "910", "911", "912", "915", "917", "918", "935"],
  },
  {
    id: 16,
    label: "AREA 16",
    counties: ["Los Angeles"],
    zipPrefixes: ["900", "902", "903", "904", "905", "913", "914", "916", "923", "928", "932"],
  },
  { id: 17, label: "AREA 17", counties: ["San Bernardino", "Riverside"] },
  { id: 18, label: "AREA 18", counties: ["Orange"] },
  { id: 19, label: "AREA 19", counties: ["San Diego"] },
];

// Fast lookups
const countyToAreaId = new Map<string, AreaId>();
const areaIdToInfo = new Map<AreaId, RatingAreaInfo>();

// ZIP to Area mapping must be initialized BEFORE seeding below to avoid TDZ
// Use 5-digit ZIPs; if you only have 3-digit prefixes, register each prefix
// with wildcard via registerZipPrefixToArea.
const zipToAreaId = new Map<string, AreaId>();
const zipPrefixToAreaId = new Map<string, AreaId>(); // 3-digit prefixes
for (const info of CA_RATING_AREAS) {
  areaIdToInfo.set(info.id, info);
  for (const county of info.counties) {
    countyToAreaId.set(normalizeCounty(county), info.id);
  }
  // seed from in-structure zips/prefixes
  if (Array.isArray(info.zips)) {
    for (const z of info.zips) registerZipToArea(z, info.id);
  }
  if (Array.isArray(info.zipPrefixes)) {
    for (const p of info.zipPrefixes) registerZipPrefixToArea(p, info.id);
  }
}

// Register from provider data at runtime (additional mappings)

export function registerZipToArea(zip: string, id: AreaId) {
  const z = normalizeZip(zip);
  if (z) zipToAreaId.set(z, id);
}

export function registerZipPrefixToArea(prefix3: string, id: AreaId) {
  const p = normalizeZipPrefix(prefix3);
  if (p) zipPrefixToAreaId.set(p, id);
}

export function bulkRegisterZipToArea(entries: Array<{ zip: string; id: AreaId }>) {
  for (const e of entries) registerZipToArea(e.zip, e.id);
}

export function bulkRegisterZipPrefixToArea(entries: Array<{ prefix3: string; id: AreaId }>) {
  for (const e of entries) registerZipPrefixToArea(e.prefix3, e.id);
}

// Lookups
export function getAreaById(id: AreaId): RatingAreaInfo | undefined {
  return areaIdToInfo.get(id);
}

export function mapWordBrownAreaLabelToId(label: string): AreaId | undefined {
  const m = String(label).trim().match(/area\s*(\d{1,2})/i);
  if (!m) return undefined;
  const num = Number(m[1]) as AreaId;
  return areaIdToInfo.has(num as AreaId) ? (num as AreaId) : undefined;
}

// Additional prefixes or 5-digit zips can be registered at runtime using the
// bulkRegisterZipPrefixToArea / bulkRegisterZipToArea functions.

export function findAreaByCounty(countyName: string): RatingAreaInfo | undefined {
  const id = countyToAreaId.get(normalizeCounty(countyName));
  return id ? areaIdToInfo.get(id) : undefined;
}

export function findAreaByZip(zip: string): RatingAreaInfo | undefined {
  const z = normalizeZip(zip);
  if (!z) return undefined;
  // exact 5-digit match first
  const idExact = zipToAreaId.get(z);
  if (idExact) return areaIdToInfo.get(idExact);
  // fallback: 3-digit prefix
  const p3 = z.substring(0, 3);
  const idPrefix = zipPrefixToAreaId.get(p3);
  if (idPrefix) return areaIdToInfo.get(idPrefix);
  return undefined;
}

export function listAreas(): RatingAreaInfo[] {
  return CA_RATING_AREAS.slice();
}

export function listCountiesByAreaId(id: AreaId): string[] {
  return areaIdToInfo.get(id)?.counties ?? [];
}

// Helpers
function normalizeCounty(name: string): string {
  return String(name).trim().toLowerCase().replace(/ county$/i, "");
}

function normalizeZip(zip: string): string | null {
  const z = String(zip).trim();
  return /^\d{5}$/.test(z) ? z : null;
}

function normalizeZipPrefix(prefix3: string): string | null {
  const p = String(prefix3).trim();
  return /^\d{3}$/.test(p) ? p : null;
}

